// Staff notes on a client — a running log. Big editor on top, newest note first
// below. Each note keeps its author + time; the author (or a manager) can edit
// or delete. Backed by /api/clients/:id/notes.
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi, type ClientNote } from "../lib/api";

function when(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export function ClientNotes({ clientId, legacyNote, currentUserId }: {
  clientId: string;
  /** clients.notes — the single free-text field from intake / Edit client. Shown read-only if set. */
  legacyNote: string | null;
  currentUserId: string | null;
}) {
  const [notes, setNotes] = useState<ClientNote[] | null>(null);
  const [canWrite, setCanWrite] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const gen = useRef(0);

  useEffect(() => {
    const g = ++gen.current;
    setNotes(null); setDraft(""); setErr(null); setEditingId(null); setConfirmDeleteId(null);
    clientsApi.listNotes(clientId)
      .then((r) => { if (g === gen.current) { setNotes(r.notes); setCanWrite(r.can_write); } })
      .catch((e: any) => { if (g === gen.current) { setNotes([]); setErr(e?.body?.message || e?.message || "Could not load notes"); } });
  }, [clientId]);

  async function add() {
    const text = draft.trim();
    if (!text) return;
    const g = gen.current;
    setBusy(true); setErr(null);
    try {
      const n = await clientsApi.addNote(clientId, text);
      if (g !== gen.current) return;
      setNotes((cur) => [n, ...(cur ?? [])]);
      setDraft("");
    } catch (e: any) {
      if (g === gen.current) setErr(e?.body?.message || e?.message || "Could not save the note");
    } finally { if (g === gen.current) setBusy(false); }
  }

  async function saveEdit(noteId: string) {
    const text = editDraft.trim();
    if (!text) return;
    const g = gen.current;
    setBusy(true); setErr(null);
    try {
      const n = await clientsApi.updateNote(clientId, noteId, text);
      if (g !== gen.current) return;
      setNotes((cur) => (cur ?? []).map((x) => (x.id === noteId ? n : x)));
      setEditingId(null);
    } catch (e: any) {
      if (g === gen.current) setErr(e?.body?.message || e?.message || "Could not update the note");
    } finally { if (g === gen.current) setBusy(false); }
  }

  async function remove(noteId: string) {
    const g = gen.current;
    setBusy(true); setErr(null);
    try {
      await clientsApi.deleteNote(clientId, noteId);
      if (g !== gen.current) return;
      setNotes((cur) => (cur ?? []).filter((x) => x.id !== noteId));
      setConfirmDeleteId(null);
    } catch (e: any) {
      if (g === gen.current) setErr(e?.body?.message || e?.message || "Could not delete the note");
    } finally { if (g === gen.current) setBusy(false); }
  }

  return (
    <div className="client-notes">
      {canWrite && (
        <section className="sub-panel" aria-labelledby="client-notes-new-title">
          <div className="sub-heading" style={{ marginBottom: 8 }}>
            <Pencil size={18} aria-hidden="true" />
            <h3 id="client-notes-new-title">Add a note</h3>
          </div>
          <p className="muted small" style={{ marginTop: 0 }}>
            Anything worth remembering about this client — what you saw on the report, what to add to the next round, what they told you.
          </p>
          <textarea
            className="client-notes__editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type your note…"
            aria-label="New note"
            disabled={busy}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); void add(); } }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <span className="muted small">{draft.trim().length ? `${draft.trim().length.toLocaleString()} characters` : "⌘/Ctrl + Enter to save"}</span>
            <button className="primary-button" onClick={add} disabled={busy || !draft.trim()}>{busy ? "Saving…" : "Save note"}</button>
          </div>
        </section>
      )}

      {err && <p className="error-text" role="alert">{err}</p>}

      {legacyNote && (
        <section className="sub-panel" style={{ marginTop: 12 }}>
          <p className="field-label" style={{ margin: 0 }}>Profile note</p>
          <p className="muted small" style={{ margin: "2px 0 6px" }}>From intake / the Edit client form.</p>
          <div className="client-notes__body">{legacyNote}</div>
        </section>
      )}

      <div className="client-notes__list" aria-live="polite">
        {notes === null && <p className="muted small">Loading notes…</p>}
        {notes && notes.length === 0 && !legacyNote && <p className="muted small">No notes yet.</p>}
        {notes && notes.map((n) => {
          const mine = !!currentUserId && n.author_user_id === currentUserId;
          const editing = editingId === n.id;
          return (
            <article className="client-notes__item" key={n.id}>
              <header className="client-notes__meta">
                <strong>{n.author_name || "Staff"}</strong>
                <span className="muted small">
                  {when(n.created_at)}{n.updated_at !== n.created_at ? ` · edited ${when(n.updated_at)}` : ""}
                </span>
                {canWrite && !editing && (
                  <span className="client-notes__actions">
                    <button type="button" className="ghost-button" title={mine ? "Edit" : "Edit (managers can edit any note)"} onClick={() => { setEditingId(n.id); setEditDraft(n.body); setConfirmDeleteId(null); }} disabled={busy}>
                      <Pencil size={13} aria-hidden="true" /> Edit
                    </button>
                    {confirmDeleteId === n.id ? (
                      <>
                        <button type="button" className="primary-button" style={{ background: "#b42318" }} onClick={() => remove(n.id)} disabled={busy}>Delete</button>
                        <button type="button" className="ghost-button" onClick={() => setConfirmDeleteId(null)} disabled={busy}>Keep</button>
                      </>
                    ) : (
                      <button type="button" className="ghost-button" title="Delete" onClick={() => setConfirmDeleteId(n.id)} disabled={busy}>
                        <Trash2 size={13} aria-hidden="true" /> Delete
                      </button>
                    )}
                  </span>
                )}
              </header>
              {editing ? (
                <>
                  <textarea className="client-notes__editor" value={editDraft} onChange={(e) => setEditDraft(e.target.value)} aria-label="Edit note" disabled={busy} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button className="primary-button" onClick={() => saveEdit(n.id)} disabled={busy || !editDraft.trim()}>Save changes</button>
                    <button className="ghost-button" onClick={() => setEditingId(null)} disabled={busy}>Cancel</button>
                  </div>
                </>
              ) : (
                <div className="client-notes__body">{n.body}</div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
